import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import Modal from './Modal';
import Button from './Button';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout
const CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

export default function SessionTimeoutWarning() {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { signOut, lastActivity, updateLastActivity } = useAuthStore();
  const navigate = useNavigate();

  const handleExtendSession = useCallback(() => {
    updateLastActivity();
    setShowWarning(false);
  }, [updateLastActivity]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  useEffect(() => {
    const checkSession = () => {
      if (!lastActivity) return;

      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeUntilTimeout = SESSION_TIMEOUT_MS - timeSinceActivity;

      if (timeUntilTimeout <= 0) {
        // Session expired
        handleSignOut();
        return;
      }

      if (timeUntilTimeout <= WARNING_BEFORE_MS) {
        // Show warning
        setShowWarning(true);
        setTimeRemaining(Math.ceil(timeUntilTimeout / 1000));
      } else {
        setShowWarning(false);
      }
    };

    // Initial check
    checkSession();

    // Set up interval
    const interval = setInterval(checkSession, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [lastActivity, handleSignOut]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdownInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [showWarning, handleSignOut]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={showWarning}
      onClose={handleExtendSession}
      title="Session Expiring Soon"
      size="sm"
    >
      <div className="text-center py-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <p className="text-gray-600 mb-4">
          Your session will expire in{' '}
          <span className="font-bold text-gray-900">{formatTime(timeRemaining)}</span>
        </p>

        <p className="text-sm text-gray-500 mb-6">
          For security, you'll be automatically signed out after 30 minutes of inactivity.
        </p>

        <div className="flex gap-3 justify-center">
          <Button variant="secondary" onClick={handleSignOut}>
            Sign Out Now
          </Button>
          <Button onClick={handleExtendSession}>
            Stay Signed In
          </Button>
        </div>
      </div>
    </Modal>
  );
}
